import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useWebSocket } from '../contexts/WebSocketContext';
import { useNotificationContext } from '../contexts/NotificationContext';
import TeacherPreferences from './TeacherPreferences';
import DeleteConfirmationModal from './DeleteConfirmationModal';
import useCascadeDelete from '../hooks/useCascadeDelete';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

const Teachers = () => {
  const { showSuccess, showError } = useNotificationContext();
  const { deleteTeacher, loading: deleteLoading } = useCascadeDelete();
  const [teachers, setTeachers] = useState([]);
  const [filteredTeachers, setFilteredTeachers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [departments, setDepartments] = useState([]);
  const [grades, setGrades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingTeacher, setEditingTeacher] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState({ show: false, teacherId: null, teacherName: '' });
  const [showPreferences, setShowPreferences] = useState(false);
  const [selectedTeacher, setSelectedTeacher] = useState(null);
  
  // Filter states
  const [selectedDepartment, setSelectedDepartment] = useState('');
  const [selectedGrade, setSelectedGrade] = useState('');
  
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    grade: '',
    specialization: '',
    departments: []
  });

  useEffect(() => {
    fetchTeachers();
    fetchDepartments();
    fetchGrades();
  }, []);

  // WebSocket message handler
  const handleWebSocketMessage = useCallback((data) => {
    if (data.type === 'connection') {
      console.log('Connection message:', data.message);
    } else if (data.type === 'teacher_updated') {
      console.log('🔄 Teacher update reçu:', data.data?.action, data.data);
      // Force immediate refresh
      setTimeout(() => {
        console.log('🔄 Refreshing teachers list...');
        fetchTeachers();
      }, 100);
    }
  }, []);

  // Use WebSocket context
  const { isConnected } = useWebSocket('teachers', handleWebSocketMessage);

  const fetchTeachers = async () => {
    try {
      console.log('🔄 Fetching teachers...');
      // Add cache-busting parameter
      const response = await axios.get(`${API_URL}/api/teachers?t=${Date.now()}`);
      console.log('✅ Teachers fetched:', response.data.length, 'teachers');
      setTeachers(response.data);
      setFilteredTeachers(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Erreur lors du chargement des enseignants:', error);
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    const term = e.target.value.toLowerCase();
    setSearchTerm(term);
    applyFilters(term, selectedDepartment, selectedGrade);
  };

  const handleDepartmentFilter = (e) => {
    setSelectedDepartment(e.target.value);
    applyFilters(searchTerm, e.target.value, selectedGrade);
  };

  const handleGradeFilter = (e) => {
    setSelectedGrade(e.target.value);
    applyFilters(searchTerm, selectedDepartment, e.target.value);
  };

  const applyFilters = (term, dept, grade) => {
    const filtered = teachers.filter(teacher => {
      // Search term filter
      const matchesSearch = !term || (
        teacher.firstName.toLowerCase().includes(term) ||
        teacher.lastName.toLowerCase().includes(term) ||
        teacher.email.toLowerCase().includes(term) ||
        teacher.phone?.toLowerCase().includes(term) ||
        teacher.specialization?.toLowerCase().includes(term) ||
        teacher.grade?.name.toLowerCase().includes(term) ||
        teacher.departments?.some(d => d.name.toLowerCase().includes(term))
      );

      // Department filter
      const matchesDepartment = !dept || teacher.departments?.some(d => d._id === dept);

      // Grade filter
      const matchesGrade = !grade || teacher.grade?._id === grade;

      return matchesSearch && matchesDepartment && matchesGrade;
    });
    setFilteredTeachers(filtered);
  };

  const fetchDepartments = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/departments`);
      setDepartments(response.data);
    } catch (error) {
      console.error('Erreur lors du chargement des départements:', error);
    }
  };

  const fetchGrades = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/grades`);
      setGrades(response.data);
    } catch (error) {
      console.error('Erreur lors du chargement des grades:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const config = {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      };
      
      if (editingTeacher) {
        await axios.put(`${API_URL}/api/teachers/${editingTeacher._id}`, formData, config);
        showSuccess('Enseignant modifié avec succès');
      } else {
        await axios.post(`${API_URL}/api/teachers`, formData, config);
        showSuccess('Enseignant ajouté avec succès');
      }
      
      closeModal();
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      showError('Erreur lors de la sauvegarde de l\'enseignant');
    }
  };

  const confirmDelete = (teacher) => {
    setDeleteConfirm({ 
      show: true, 
      teacherId: teacher._id, 
      teacherName: `${teacher.firstName} ${teacher.lastName}` 
    });
  };

  const handleConfirmDelete = async () => {
    if (deleteConfirm.teacherId) {
      await deleteTeacher(deleteConfirm.teacherId);
      fetchTeachers(); // Refresh the list
    }
    setDeleteConfirm({ show: false, teacherId: null, teacherName: '' });
  };

  const cancelDelete = () => {
    setDeleteConfirm({ show: false, teacherId: null, teacherName: '' });
  };

  const openModal = (teacher = null) => {
    setEditingTeacher(teacher);
    setFormData(teacher ? {
      firstName: teacher.firstName,
      lastName: teacher.lastName,
      email: teacher.email,
      phone: teacher.phone || '',
      grade: teacher.grade?._id || '',
      specialization: teacher.specialization || '',
      departments: teacher.departments?.map(d => d._id) || []
    } : {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      grade: grades.length > 0 ? grades[0]._id : '',
      specialization: '',
      departments: []
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingTeacher(null);
    setFormData({ firstName: '', lastName: '', email: '', phone: '', grade: '', specialization: '', departments: [] });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'departments') {
      const selectedOptions = Array.from(e.target.selectedOptions, option => option.value);
      setFormData({ ...formData, departments: selectedOptions });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  if (loading) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto mb-2"></div>
        <p className="text-sm text-gray-600">Chargement...</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
          <div className="flex items-center gap-3 mb-4 sm:mb-0">
            <h1 className="text-xl font-semibold text-gray-900">Enseignants</h1>
            <span className={`px-2 py-1 text-xs rounded-full ${
              isConnected 
                ? 'bg-green-100 text-green-700' 
                : 'bg-red-100 text-red-700'
            }`}>
              {isConnected ? '🟢 Live' : '🔴 Manual'}
            </span>
          </div>
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative">
              <input
                type="text"
                placeholder="Rechercher un enseignant..."
                value={searchTerm}
                onChange={handleSearch}
                className="w-full sm:w-64 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
            <button 
              onClick={() => openModal()} 
              className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 focus:ring-2 focus:ring-primary-500"
            >
              + Ajouter
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Filtrer par département</label>
              <select
                value={selectedDepartment}
                onChange={handleDepartmentFilter}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="">Tous les départements</option>
                {departments.map(dept => (
                  <option key={dept._id} value={dept._id}>
                    {dept.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Filtrer par grade</label>
              <select
                value={selectedGrade}
                onChange={handleGradeFilter}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="">Tous les grades</option>
                {grades.map(grade => (
                  <option key={grade._id} value={grade._id}>
                    {grade.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex items-end">
              <button
                onClick={() => {
                  setSelectedDepartment('');
                  setSelectedGrade('');
                  setSearchTerm('');
                  setFilteredTeachers(teachers);
                }}
                className="w-full px-3 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 focus:ring-2 focus:ring-gray-500"
              >
                🔄 Réinitialiser les filtres
              </button>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredTeachers.map(teacher => (
            <div key={teacher._id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 hover:shadow-md transition-shadow">
              <div className="flex justify-between items-start mb-3">
                <div className="flex-1">
                  <h3 className="text-lg font-medium text-gray-900 mb-1">
                    {teacher.firstName} {teacher.lastName}
                  </h3>
                  <p className="text-sm text-gray-600 mb-1">📧 {teacher.email}</p>
                  {teacher.phone && <p className="text-sm text-gray-600 mb-2">📞 {teacher.phone}</p>}
                </div>
                <div className="flex gap-1">
                  <button 
                    onClick={() => {
                      setSelectedTeacher(teacher);
                      setShowPreferences(true);
                    }}
                    className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                    title="Préférences IA"
                  >
                    🤖
                  </button>
                  <button 
                    onClick={() => openModal(teacher)}
                    className="p-1.5 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded transition-colors"
                  >
                    ✏️
                  </button>
                  <button 
                    onClick={() => confirmDelete(teacher)}
                    className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                  >
                    🗑️
                  </button>
                </div>
              </div>
              
              <div className="space-y-2">
                {teacher.grade && (
                  <div className="flex items-center text-sm text-gray-600">
                    <span className="w-5">🏅</span>
                    <span>{teacher.grade.name}</span>
                  </div>
                )}
                {teacher.specialization && (
                  <div className="flex items-center text-sm text-gray-600">
                    <span className="w-5">🎓</span>
                    <span>{teacher.specialization}</span>
                  </div>
                )}
                {teacher.departments?.length > 0 && (
                  <div className="flex items-start text-sm text-gray-600">
                    <span className="w-5 mt-0.5">🏢</span>
                    <span className="flex-1">{teacher.departments.map(d => d.name).join(', ')}</span>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {filteredTeachers.length === 0 && teachers.length > 0 && (
          <div className="text-center py-16">
            <div className="text-6xl mb-4">🔍</div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">Aucun résultat</h3>
            <p className="text-gray-600">Aucun enseignant trouvé pour "<strong>{searchTerm}</strong>"</p>
          </div>
        )}
        
        {teachers.length === 0 && (
          <div className="text-center py-16">
            <div className="text-6xl mb-4">👨‍🏫</div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">Aucun enseignant</h3>
            <p className="text-gray-600">Cliquez sur "Ajouter" pour commencer.</p>
          </div>
        )}

        {showModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50" onClick={closeModal}>
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
              <div className="flex justify-between items-center p-4 border-b">
                <h3 className="text-lg font-medium text-gray-900">{editingTeacher ? 'Modifier Enseignant' : 'Ajouter Enseignant'}</h3>
                <button onClick={closeModal} className="text-gray-400 hover:text-gray-600">×</button>
              </div>
              <form onSubmit={handleSubmit} className="p-4 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Prénom</label>
                    <input
                      type="text"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleChange}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Nom</label>
                    <input
                      type="text"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleChange}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Téléphone</label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      placeholder="Ex: +216 12 345 678"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Grade</label>
                    <select
                      name="grade"
                      value={formData.grade}
                      onChange={handleChange}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      required
                    >
                      <option value="">Sélectionner un grade</option>
                      {grades.map(grade => (
                        <option key={grade._id} value={grade._id}>
                          {grade.name} ({grade.abbreviation})
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Spécialisation</label>
                  <input
                    type="text"
                    name="specialization"
                    value={formData.specialization}
                    onChange={handleChange}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="Ex: Mathématiques, Informatique..."
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Départements</label>
                  <select
                    name="departments"
                    multiple
                    value={formData.departments}
                    onChange={handleChange}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent h-24"
                  >
                    {departments.map(dept => (
                      <option key={dept._id} value={dept._id}>
                        {dept.name}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">Maintenez Ctrl/Cmd pour sélectionner plusieurs départements</p>
                </div>
                <div className="flex gap-3 pt-4">
                  <button 
                    type="submit" 
                    className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 focus:ring-2 focus:ring-primary-500"
                  >
                    {editingTeacher ? 'Modifier' : 'Ajouter'}
                  </button>
                  {editingTeacher && (
                    <button
                      type="button"
                      onClick={() => confirmDelete(editingTeacher)}
                      className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 focus:ring-2 focus:ring-red-500"
                    >
                      Supprimer
                    </button>
                  )}
                  <button 
                    type="button" 
                    onClick={closeModal} 
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 focus:ring-2 focus:ring-gray-500"
                  >
                    Annuler
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        <DeleteConfirmationModal
          isOpen={deleteConfirm.show}
          onClose={cancelDelete}
          onConfirm={handleConfirmDelete}
          title="Supprimer l'enseignant"
          message={`Êtes-vous sûr de vouloir supprimer ${deleteConfirm.teacherName} ? Toutes les séances associées seront également supprimées.`}
          loading={deleteLoading}
        />

        {showPreferences && selectedTeacher && (
          <TeacherPreferences
            teacher={selectedTeacher}
            onClose={() => {
              setShowPreferences(false);
              setSelectedTeacher(null);
            }}
          />
        )}
      </div>
    </div>
  );
};

export default Teachers;